/**
 * Amex Offer page (sections/amex-offer.liquid)
 *  - countdown chip in the hero (starts / days left / ended)
 *  - one-click "add bundle" buttons: adds every variant in the bundle via the
 *    theme cart store (window._cart) and opens the cart drawer.
 */
(() => {
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  function initCountdown(root) {
    const chip = root.querySelector('[data-amex-countdown]');
    if (!chip) return;
    const start = new Date(`${chip.dataset.start}T00:00:00`);
    const end = new Date(`${chip.dataset.end}T23:59:59`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
    const now = new Date();
    let text = '';
    if (now < start) text = `Starts ${fmt(start)}`;
    else if (now > end) text = 'Offer ended';
    else {
      const days = Math.ceil((end - now) / 864e5);
      text = days <= 1 ? 'Last day' : `${days} days left`;
    }
    chip.textContent = text;
    chip.hidden = false;
  }

  async function addBundle(btn) {
    if (btn.dataset.busy) return;
    let items;
    try {
      // Proteins and their flavor packs share a placeholder id from Liquid. Give each
      // click a fresh one so the cart nests the flavors under the right protein.
      const pid = Array.from({ length: 6 }, () => 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]).join('');
      items = JSON.parse(btn.dataset.items.replaceAll('AMEXPID', pid));
      // Apply the flavor / option the customer picked on the card. Each select points
      // at its line in the payload; choices are same-price, so totals do not change.
      const card = btn.closest('.ax-bundle');
      (card ? card.querySelectorAll('[data-amex-variant]') : []).forEach((sel) => {
        const item = items[Number(sel.dataset.amexIndex)];
        const id = Number(sel.value);
        if (item && id) item.id = id;
      });
      // Same flavor picked for both packs: send one line of two. _p_quantity tells the
      // cart how many packs go with each protein when the protein quantity changes.
      const merged = [];
      items.forEach((item) => {
        const link = item.properties && item.properties._p_id_link;
        const twin = link && merged.find((m) => m.id === item.id && m.properties && m.properties._p_id_link === link);
        if (twin) {
          twin.quantity += item.quantity;
          twin.properties._p_quantity = String(twin.quantity);
        } else {
          merged.push(item);
        }
      });
      items = merged;
    } catch { return; }
    const label = btn.textContent;
    btn.dataset.busy = '1';
    btn.classList.add('is-loading');
    btn.textContent = 'Adding…';
    try {
      if (window._cart && typeof window._cart.add === 'function') {
        const res = await window._cart.add({ items });
        if (res && res.cart_error) throw new Error('cart_error');
        window._stores?.modal?.setId?.('cart-drawer');
      } else {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!r.ok) throw new Error('cart_error');
        window.location.href = '/cart';
        return;
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'amex_bundle_add',
        bundle_name: btn.dataset.bundle || '',
        purchase_type: btn.dataset.mode || 'onetime',
        value: Number(btn.dataset.value) || 0,
        item_count: items.length,
      });
      btn.textContent = 'Added to cart';
    } catch {
      btn.textContent = 'Could not add. Try again';
    } finally {
      setTimeout(() => {
        btn.textContent = label;
        btn.classList.remove('is-loading');
        delete btn.dataset.busy;
      }, 2200);
    }
  }

  // Bundle tabs: one group panel visible at a time.
  function activateTab(root, n, { focus = false } = {}) {
    const tabs = root.querySelectorAll('[data-amex-tab]');
    if (!tabs.length) return;
    tabs.forEach((tab) => {
      const on = tab.dataset.amexTab === String(n);
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
      if (on && focus) tab.focus();
    });
    root.querySelectorAll('[data-amex-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.amexPanel !== String(n);
    });
  }

  function initTabs(root) {
    const tabs = Array.from(root.querySelectorAll('[data-amex-tab]'));
    if (!tabs.length) return;
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => {
        activateTab(root, tab.dataset.amexTab);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'amex_bundle_tab', tab_label: tab.textContent.trim() });
      });
      tab.addEventListener('keydown', (e) => {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        let next = null;
        if (step) next = tabs[(i + step + tabs.length) % tabs.length];
        else if (e.key === 'Home') next = tabs[0];
        else if (e.key === 'End') next = tabs[tabs.length - 1];
        if (!next) return;
        e.preventDefault();
        activateTab(root, next.dataset.amexTab, { focus: true });
      });
    });
    // Deep link: /amex#amex-group-2 opens that tab.
    const fromHash = () => {
      const m = /^#amex-group-(\d+)$/.exec(window.location.hash);
      if (m && root.querySelector(`[data-amex-panel="${m[1]}"]`)) activateTab(root, m[1]);
    };
    fromHash();
    window.addEventListener('hashchange', fromHash);
  }

  // Reminder email: subscribe straight to the Klaviyo list (client subscriptions API).
  // If the keys are missing or the request fails, the native Shopify customer form
  // submits as usual, so a sign-up is never lost.
  function initEmail(root) {
    const panel = root.querySelector('[data-amex-email]');
    const form = panel && panel.querySelector('form');
    if (!form) return;
    const company = panel.dataset.klaviyoCompany;
    const list = panel.dataset.klaviyoList;
    if (!company || !list) return;
    let fallback = false;
    form.addEventListener('submit', async (e) => {
      if (fallback) return;
      e.preventDefault();
      const input = form.querySelector('input[type="email"]');
      const button = form.querySelector('button[type="submit"]');
      const error = form.querySelector('[data-amex-email-error]');
      const email = (input && input.value || '').trim();
      if (!email) return;
      const label = button && button.textContent;
      if (button) { button.disabled = true; button.textContent = 'Sending...'; }
      if (error) error.hidden = true;
      try {
        const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(company)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: panel.dataset.klaviyoSource || 'Amex Offer page',
                profile: { data: { type: 'profile', attributes: { email, properties: { amex_offer_reminder: true, amex_offer_signup_page: window.location.pathname } } } },
              },
              relationships: { list: { data: { type: 'list', id: list } } },
            },
          }),
        });
        if (!res.ok) throw new Error(`klaviyo ${res.status}`);
        const fields = form.querySelector('[data-amex-email-fields]');
        const success = form.querySelector('[data-amex-email-success]');
        if (fields) fields.hidden = true;
        if (success) success.hidden = false;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'amex_email_signup', list_id: list });
      } catch {
        // Hand over to the Shopify customer form rather than dropping the sign-up.
        fallback = true;
        if (button) { button.disabled = false; button.textContent = label; }
        form.submit();
      }
    });
  }

  function init(root) {
    if (!root || root.dataset.amexInit) return;
    root.dataset.amexInit = '1';
    initCountdown(root);
    initTabs(root);
    initEmail(root);
    root.querySelectorAll('[data-amex-add]').forEach((btn) => {
      btn.addEventListener('click', () => addBundle(btn));
    });
  }

  const boot = () => document.querySelectorAll('[data-section-type="amex-offer"]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor: selecting a group or bundle block reveals the tab it lives in.
  document.addEventListener('shopify:block:select', (e) => {
    const panel = e.target && e.target.closest('[data-amex-panel]');
    const root = panel && panel.closest('[data-section-type="amex-offer"]');
    if (root) activateTab(root, panel.dataset.amexPanel);
  });

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target && e.target.querySelector('[data-section-type="amex-offer"]');
    if (root) init(root);
  });
})();
